import './applyPurchaseTypePage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import LoadingButton from '@mui/lab/LoadingButton';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import ThemeProvider from '@mui/material/styles/ThemeProvider';
import { PurchaseType } from '@thesavingsgroup/enums/PurchaseType';
import { isEmpty } from 'lodash';
import { useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioGroup } from '../../../components/RadioGroup/RadioGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): JSX.Element {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const { purchaseType, setPurchaseType } = useApplyStore();

  const { handleSubmit, register, watch } = useForm({
    defaultValues: {
      purchaseTypeValue: purchaseType,
    },
  });

  const previousStep = (): void => {
    navigate(previous());
  };

  const radioOptionClicked = (
    event: RadioOptionClickEvent<PurchaseType>,
  ): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    const purchaseTypeValue = event.target.value;

    setIsInitialSelection(isEmpty(purchaseType) && isValidClickEvent(event));

    setPurchaseType(purchaseTypeValue);
  };

  const formSubmit = (values: FieldValues): void => {
    const { purchaseTypeValue } = values;

    if (purchaseTypeValue) {
      navigate(next());
    }
  };

  useEffect(() => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  return (
    <div className="apply-purchase-type-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.applyPurchaseType.header.root}>
            <button
              aria-label="Previous step"
              data-testid={
                locator.pages.applyPurchaseType.header.previousStepButton
              }
              type="button"
              onClick={previousStep}
            >
              <KeyboardBackspaceRoundedIcon />
            </button>
            <button
              aria-label="Toggle drawer"
              data-testid={locator.pages.applyPurchaseType.header.drawerButton}
              type="button"
              onClick={() => {
                setDrawerOpen(true);
              }}
            >
              <MenuRoundedIcon />
            </button>
          </header>

          <div className="progress-container" role="complementary">
            <LinearProgress
              aria-label="Application completed percentage"
              data-testid={locator.pages.applyPurchaseType.progress.root}
              value={useWorkflowStore.getState().computed.progress}
              variant="determinate"
            />
          </div>

          <main>
            <form
              data-testid={locator.pages.applyPurchaseType.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.applyPurchaseType.form.h1}>
                  {t('ApplyPurchaseTypePage.title')}
                </h1>
                <h2 data-testid={locator.pages.applyPurchaseType.form.h2}>
                  {t('ApplyPurchaseTypePage.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  <RadioGroup
                    defaultValue={purchaseType}
                    labelPlacement="below"
                    name="purchaseTypeValue"
                    options={[
                      {
                        label: t('ApplyPurchaseTypePage.dealer'),
                        value: PurchaseType.DEALER,
                        testId:
                          locator.pages.applyPurchaseType.form.radioOption1,
                      },
                      {
                        label: t('ApplyPurchaseTypePage.privateParty'),
                        value: PurchaseType.PRIVATE_PARTY,
                        testId:
                          locator.pages.applyPurchaseType.form.radioOption2,
                      },
                    ]}
                    register={register}
                    testId={locator.pages.applyPurchaseType.form.radioGroupRoot}
                    variant="inline"
                    watch={watch}
                    onClick={radioOptionClicked}
                  />
                </div>

                <div style={{ marginBottom: '3rem' }}>
                  <LoadingButton
                    color="primary"
                    data-testid={
                      locator.pages.applyPurchaseType.form.submitButton
                    }
                    disabled={isEmpty(purchaseType)}
                    startIcon={<></>}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {t('ApplyPurchaseTypePage.continue')}
                  </LoadingButton>
                </div>
              </div>
              <footer data-testid={locator.pages.applyPurchaseType.footer.root}>
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'ApplyPurchaseTypePage';
